import { useEffect, useState } from "react";
import type { SlotProps } from "../../../core/platform/registry/registry";
import { db } from "../../../core/data/local/db";
import { ticketQueue } from "..";

/**
 * The order number shown at sale completion (T031, FR-225).
 *
 * RENDERED VIA `till.postSale`, THE EXISTING POST-SALE SLOT — no new core
 * surface needed here, unlike T022's shift-close summary. Core already
 * passes the just-finalized sale's `documentNumber` into this slot; nothing
 * else about the receipt screen needs to know a ticket module exists.
 *
 * NO RACE WITH THE ENQUEUE. `commitSale()` awaits `coreEvents.emit(
 * "sale.finalized", ...)`, which awaits every subscriber — including this
 * module's, which awaits `TicketQueue.enqueue()` before returning. By the
 * time the receipt overlay (and this slot) renders, the job already exists
 * in `ticketQueue.jobs`, with its order number assigned.
 *
 * RENDERS NOTHING when there is no job — no printer ever paired, or the
 * document number does not resolve to a job for some other reason. A blank
 * badge would be a confusing half-feature; absence here matches the ticket
 * itself never having printed.
 */
export function TicketOrderNumberBadge({ saleId: documentNumber }: SlotProps) {
  const [orderNumber, setOrderNumber] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (typeof documentNumber !== "string") return;

    void db.sales
      .where("documentNumber")
      .equals(documentNumber)
      .first()
      .then((sale) => {
        if (cancelled || !sale) return;
        const job = ticketQueue.jobs.find((candidate) => candidate.saleId === sale.id);
        if (job) setOrderNumber(job.document.orderNumber);
      });

    return () => {
      cancelled = true;
    };
  }, [documentNumber]);

  if (!orderNumber) return null;

  return <p className="tillordernumber">Order {orderNumber}</p>;
}

export default TicketOrderNumberBadge;
